import React from 'react';

export type TableColumn<T> = {
  key: keyof T | string;
  label: string;
  render?: (row: T, idx: number) => React.ReactNode;
  className?: string;
  headerClassName?: string;
};

export type TableAction<T> = {
  label: string;
  color?: string;
  hover?: string;
  onClick?: (row: T) => void;
  href?: string | ((row: T) => string);
};

export type TableProps<T> = {
  data: T[];
  columns: TableColumn<T>[];
  checked?: number[];
  onCheck?: (idx: number) => void;
  onCheckAll?: () => void;
  showCheckbox?: boolean;
  actions?: TableAction<T>[];
  renderSortLink?: (key: string, label: string) => React.ReactNode;
  actionColWidthClass?: string; // 追加: 操作列の幅クラス
};

function Table<T extends { [key: string]: unknown }>({
  data,
  columns,
  checked = [],
  onCheck,
  onCheckAll,
  showCheckbox = false,
  actions = [],
  renderSortLink,
  actionColWidthClass = 'w-48', // デフォルト値
}: TableProps<T>) {
  return (
    <table className="w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          {showCheckbox && (
            <th className="w-8 px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
              <input
                type="checkbox"
                checked={checked.length === data.length && data.length > 0}
                onChange={onCheckAll}
                className="accent-blue-700"
              />
            </th>
          )}
          {columns.map((col) => (
            <th
              key={col.key as string}
              className={`px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase ${col.headerClassName || ''}`}
            >
              {renderSortLink ? renderSortLink(col.key as string, col.label) : col.label}
            </th>
          ))}
          {actions.length > 0 && (
            <th className={`${actionColWidthClass} px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase`}>操作</th>
          )}
        </tr>
      </thead>
      <tbody>
        {data.map((row, idx) => (
          <tr key={String(row.id ?? row.loginId ?? idx)} className={idx % 2 === 0 ? 'bg-gray-50' : ''}>
            {showCheckbox && (
              <td className="px-2 py-2">
                <input
                  type="checkbox"
                  checked={checked.includes(idx)}
                  onChange={() => onCheck && onCheck(idx)}
                  className="accent-blue-700"
                />
              </td>
            )}
            {columns.map((col) => (
              <td key={col.key as string} className={`px-4 py-2 ${col.className || ''}`}>
                {col.render ? col.render(row, idx) : String(row[col.key as keyof T] ?? '')}
              </td>
            ))}
            {actions.length > 0 && (
              <td className={`flex flex-wrap gap-1 px-2 py-1 whitespace-normal ${actionColWidthClass}`}>
                {actions.map((action) => {
                  const href = typeof action.href === 'function' ? action.href(row) : action.href;
                  return href ? (
                    <a
                      key={action.label}
                      href={href}
                      className={`text-[12px] ${action.color || ''} ${action.hover || ''} px-1 py-0.5 rounded border border-transparent hover:bg-gray-100 transition`}
                      onClick={e => { if (action.onClick) { e.preventDefault(); action.onClick(row); } }}
                    >
                      {action.label}
                    </a>
                  ) : (
                    <button
                      key={action.label}
                      className={`text-[12px] ${action.color || ''} ${action.hover || ''} px-1 py-0.5 rounded border border-transparent hover:bg-gray-100 transition`}
                      onClick={() => action.onClick && action.onClick(row)}
                    >
                      {action.label}
                    </button>
                  );
                })}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default Table;
